// src/views/reports/TeamReportView.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore'; 
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, TrendingUp, Calendar, Download, RefreshCw } from 'lucide-react';
import { startOfMonth, endOfMonth, format, parseISO } from 'date-fns';
import { formatCurrency } from '../../utils/formatters';
import { exportToCSV } from '../../utils/csvExport';
import { canSeeLaborCost, rowsWithCostRates } from '../../utils/jobCosts'; // s6costing-4d8a2f63
import { useOrgJobCosts } from '../../hooks/useJobCosts'; // s6costing-4d8a2f63

const TeamReportView = ({ currentUserProfile }) => {
    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    // Default to This Month
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

    // --- DATA FETCHING ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        const fetchData = async () => {
            setLoading(true);
            try {
                // 1. Fetch Employees from top-level users collection
                // (users are NOT in an org subcollection - they live in /users with organizationId field)
                const empQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', orgId),
                    where('status', 'in', ['active', 'Active'])
                );
                const empSnapshot = await getDocs(empQuery);
                const empData = empSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setEmployees(empData);

                // 2. Fetch Completed Jobs
                const qJobs = query(
                    collection(db, `organizations/${orgId}/jobs`),
                    where('status', '==', 'Completed')
                );
                
                const jobSnapshot = await getDocs(qJobs);
                const jobData = jobSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })); // s6costing-docid: doc id wins over a stale stored `id` field

                // Sort in Browser (Newest First) using same date priority as filter
                jobData.sort((a, b) => {
                    const rawA = a.completedAt || a.updatedAt || a.endAt;
                    const rawB = b.completedAt || b.updatedAt || b.endAt;
                    const dateA = rawA?.toDate ? rawA.toDate() : new Date(rawA || 0);
                    const dateB = rawB?.toDate ? rawB.toDate() : new Date(rawB || 0);
                    return dateB - dateA;
                });

                setJobs(jobData);
            } catch (error) {
                console.error("Error loading team report:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

    }, [currentUserProfile?.organizationId, refreshKey]);

    // s6costing-4d8a2f63 — S6b: labour cost rates come from jobCosts (Owner/Admin/Manager only),
    // falling back to a not-yet-migrated row's own rate. Display-only copies; nothing is saved.
    const { ratesByJob } = useOrgJobCosts(currentUserProfile?.organizationId, canSeeLaborCost(currentUserProfile));

    // --- METRICS ENGINE ---
    const teamMetrics = useMemo(() => {
        const start = parseISO(startDate);
        const end = parseISO(endDate);
        end.setHours(23, 59, 59, 999);

        // Filter Jobs by Date - use completedAt if available, fall back to endAt
        const filteredJobs = jobs.filter(job => {
            const raw = job.completedAt || job.updatedAt || job.endAt;
            if (!raw) return false;
            const d = raw.toDate ? raw.toDate() : new Date(raw);
            return d >= start && d <= end;
        });

        // Initialize Stats Map with known Employees
        const stats = {};
        employees.forEach(emp => {
            stats[emp.id] = {
                name: emp.name,
                jobsCount: 0,
                hoursLogged: 0,
                laborCost: 0,
                revenueGenerated: 0,
                role: emp.role || 'Employee'
            };
        });
        
        // Catch-all for truly missing data
        stats['unknown'] = { name: 'Unassigned', jobsCount: 0, hoursLogged: 0, laborCost: 0, revenueGenerated: 0, role: 'N/A' };

        // Process Jobs
        filteredJobs.forEach(job => {
            const invoiceTotal = parseFloat(job.invoiceTotal) || 0;
            const laborEntries = rowsWithCostRates(job.jobCosting?.laborEntries, ratesByJob[job.id]); // s6costing-4d8a2f63
            
            const totalJobHours = laborEntries.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0);

            laborEntries.forEach(entry => {
                let uid = entry.userId;

                // --- FIX: Dynamic Admin/Owner Detection ---
                // If the user isn't in the employee list, create a stats entry for them using the name saved on the job.
                if (uid && !stats[uid] && entry.name) {
                    stats[uid] = {
                        name: entry.name, // Use the name from the job record
                        jobsCount: 0,
                        hoursLogged: 0,
                        laborCost: 0,
                        revenueGenerated: 0,
                        role: 'Admin / Owner' // Assume they are leadership if not in employee list
                    };
                }

                // If still no ID found, bucket into 'unknown'
                if (!uid || !stats[uid]) {
                    uid = 'unknown';
                }

                const hours = parseFloat(entry.hours) || 0;
                const rate = parseFloat(entry.rate) || 0;
                
                stats[uid].hoursLogged += hours;
                stats[uid].laborCost += (hours * rate);

                // Revenue Attribution (Pro-rata based on hours worked)
                if (totalJobHours > 0 && invoiceTotal > 0) {
                    const share = hours / totalJobHours;
                    stats[uid].revenueGenerated += (invoiceTotal * share);
                }
            });

            // Increment job counts
            const uniqueWorkers = new Set(laborEntries.map(e => e.userId));
            uniqueWorkers.forEach(uid => {
                if (stats[uid]) stats[uid].jobsCount += 1;
            });
        });

        return Object.values(stats)
            .filter(s => s.hoursLogged > 0 || s.jobsCount > 0) 
            .map(s => ({
                ...s,
                netContribution: s.revenueGenerated - s.laborCost,
                efficiency: s.laborCost > 0 ? (s.revenueGenerated / s.laborCost) : 0 
            }))
            .sort((a, b) => b.revenueGenerated - a.revenueGenerated);

    }, [jobs, employees, startDate, endDate, ratesByJob]);

    // --- EXPORT ---
    const handleExport = () => {
        if (!teamMetrics.length) return alert("No data to export");
        const csvData = teamMetrics.map(emp => ({
            Employee: emp.name,
            Role: emp.role,
            Jobs_Worked: emp.jobsCount,
            Total_Hours: emp.hoursLogged.toFixed(2),
            Labor_Cost: emp.laborCost.toFixed(2),
            Revenue_Generated: emp.revenueGenerated.toFixed(2),
            Net_Contribution: emp.netContribution.toFixed(2),
            ROI_Multiplier: emp.efficiency.toFixed(2) + 'x'
        }));
        exportToCSV(csvData, `Team_Performance_${startDate}.csv`);
    };

    // --- FIX: REMOVED HARDCODED PERMISSION CHECK ---
    
    if (loading) return <div className="p-10 text-center text-gray-500">Loading performance data...</div>;

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Team Performance</h1>
                        <p className="text-gray-500 text-sm">Revenue generation and efficiency per employee</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 bg-white p-2 rounded-md border shadow-sm">
                        <Calendar size={18} className="text-gray-400" />
                        <input 
                            type="date" 
                            value={startDate} 
                            onChange={(e) => setStartDate(e.target.value)}
                            className="text-sm border-none focus:ring-0 text-gray-600"
                        />
                        <span className="text-gray-400">-</span>
                        <input 
                            type="date" 
                            value={endDate} 
                            onChange={(e) => setEndDate(e.target.value)}
                            className="text-sm border-none focus:ring-0 text-gray-600"
                        />
                    </div>
                    <button 
                        onClick={() => setRefreshKey(k => k + 1)} 
                        className="p-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                        title="Refresh Data"
                    >
                        <RefreshCw size={18} />
                    </button>
                    <button 
                        onClick={handleExport}
                        className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                    >
                        <Download size={18} /> Export
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
                {teamMetrics.map((emp) => (
                    <div key={emp.name} className="bg-white border rounded-lg shadow-sm p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:shadow-md transition-shadow">
                        
                        <div className="flex items-center gap-4 min-w-[200px]">
                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg">
                                {emp.name.charAt(0)}
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-800 text-lg">{emp.name}</h3>
                                <p className="text-sm text-gray-500">{emp.role} • {emp.jobsCount} Jobs</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 flex-grow">
                            <div>
                                <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Hours Logged</p>
                                <p className="font-medium text-gray-700">{emp.hoursLogged.toFixed(1)} hrs</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Revenue Gen.</p>
                                <p className="font-bold text-green-600">{formatCurrency(emp.revenueGenerated)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Cost (Wages)</p>
                                <p className="font-medium text-red-500">{formatCurrency(emp.laborCost)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 uppercase font-semibold mb-1">ROI Multiplier</p>
                                <div className="flex items-center gap-1">
                                    <span className={`font-bold ${emp.efficiency >= 3 ? 'text-green-600' : emp.efficiency >= 1.5 ? 'text-blue-600' : 'text-orange-500'}`}>
                                        {emp.efficiency.toFixed(2)}x
                                    </span>
                                    {emp.efficiency >= 3 && <TrendingUp size={16} className="text-green-500" />}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {teamMetrics.length === 0 && (
                    <div className="text-center py-12 bg-white border rounded-lg">
                        <Users size={48} className="mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">No completed jobs found for this period.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeamReportView;